/**
 * Validates a multi-section (accordion group) ExternalTableSchema.
 */

import type { ValidationResult, ValidationIssue } from './validator';
import type { ExternalTableSchema, SectionDef, SharedControlsDef, FilterDef, ActionDef } from './types';

function issue(
  path: string,
  message: string,
  severity: ValidationIssue['severity'],
  code: string,
): ValidationIssue {
  return { path, message, severity, code };
}

function validateFilter(filter: FilterDef, idx: number, basePath: string, issues: ValidationIssue[]): void {
  const p = `${basePath}[${idx}]`;
  if (!filter.name || typeof filter.name !== 'string') {
    issues.push(issue(`${p}.name`, 'Filter must have a non-empty name.', 'error', 'FILTER_NAME_MISSING'));
  }
  if (!filter.type || typeof filter.type !== 'string') {
    issues.push(issue(`${p}.type`, 'Filter must have a type.', 'error', 'FILTER_TYPE_MISSING'));
  }
}

function validateActionDef(action: ActionDef | undefined, path: string, issues: ValidationIssue[]): void {
  if (!action) return;
  if (!action.label || typeof action.label !== 'string') {
    issues.push(issue(`${path}.label`, 'Action must have a label.', 'error', 'ACTION_LABEL_MISSING'));
  }
  if (!action.actionType) {
    issues.push(issue(`${path}.actionType`, 'Action must have an actionType.', 'error', 'ACTION_TYPE_MISSING'));
  }
}

function validateSharedControls(controls: SharedControlsDef | undefined, issues: ValidationIssue[]): void {
  if (!controls) return;
  (controls.filters ?? []).forEach((f, i) => validateFilter(f, i, 'sharedControls.filters', issues));
  validateActionDef(controls.toolbar?.primary, 'sharedControls.toolbar.primary', issues);
  (controls.toolbar?.secondary ?? []).forEach((a, i) =>
    validateActionDef(a, `sharedControls.toolbar.secondary[${i}]`, issues),
  );
  validateActionDef(controls.import?.downloadTemplate, 'sharedControls.import.downloadTemplate', issues);
  validateActionDef(controls.import?.uploadAction, 'sharedControls.import.uploadAction', issues);
}

function validateSection(section: SectionDef, idx: number, issues: ValidationIssue[]): void {
  const p = `sections[${idx}]`;

  if (!section.id || typeof section.id !== 'string') {
    issues.push(issue(`${p}.id`, 'Section must have a non-empty id.', 'critical', 'SECTION_ID_MISSING'));
  }
  if (!section.title || typeof section.title !== 'string') {
    issues.push(issue(`${p}.title`, 'Section must have a title.', 'error', 'SECTION_TITLE_MISSING'));
  }
  if (!Array.isArray(section.columns) || section.columns.length === 0) {
    issues.push(issue(`${p}.columns`, 'Section must have at least one column.', 'critical', 'SECTION_COLUMNS_EMPTY'));
  }
  if (section.permission) {
    if (!section.permission.action) {
      issues.push(issue(`${p}.permission.action`, 'Permission must have an action.', 'error', 'PERM_ACTION_MISSING'));
    }
    if (!section.permission.resource) {
      issues.push(issue(`${p}.permission.resource`, 'Permission must have a resource.', 'error', 'PERM_RESOURCE_MISSING'));
    }
  }
  (section.dataSource?.filters ?? []).forEach((f, i) =>
    validateFilter(f, i, `${p}.dataSource.filters`, issues),
  );
}

export function validateMultiSectionSchema(schema: ExternalTableSchema): ValidationResult {
  const issues: ValidationIssue[] = [];
  const sections = schema.sections ?? [];

  if (sections.length === 0) {
    issues.push(issue('sections', 'Multi-section schema must have at least one section.', 'critical', 'SECTIONS_EMPTY'));
  }

  const seenIds = new Set<string>();
  sections.forEach((section, idx) => {
    validateSection(section, idx, issues);
    if (section.id) {
      if (seenIds.has(section.id)) {
        issues.push(
          issue(`sections[${idx}].id`, `Duplicate section id "${section.id}".`, 'error', 'SECTION_ID_DUPLICATE'),
        );
      }
      seenIds.add(section.id);
    }
  });

  validateSharedControls(schema.sharedControls, issues);

  const errors = issues.filter((i) => i.severity === 'critical' || i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}
